import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { BRAND_PATHS } from '@/components/camera/brandMarks'
import { captionFor } from '@/components/camera/captions'
import { clipLen, MAX_CLIP, MAX_ITEMS, type Item } from '@/components/camera/recordEngine'
import { recordStore, fmtBytes, usedBytes, STORE_BUDGET, pref, setPref, type StoreEntry, type StoredTake } from '@/components/camera/recordStore'
import { mmss } from '@/components/teleprompter/prompter'
import { canSaveToPhotos, copyText, installedApps, openApp, saveVideo, SHARE_APPS } from '@/lib/nativeMedia'
import type { ShareApp } from '@/lib/nativeCamera'
import type { Story } from '@/types/database'

/** A bottom sheet over Record. `lock`: the scrim does not close it (the take sheet). */
export function Sheet({ onClose, lock, children, label }: { onClose: () => void; lock?: boolean; children: ReactNode; label: string }) {
  return (
    <>
      <div className="rec-scrim" onClick={() => { if (!lock) onClose() }} />
      <div className="rec-sheet" role="dialog" aria-modal="true" aria-label={label} onPointerDown={(e) => e.stopPropagation()}>
        <div className="rec-sheet__grab" />
        <div className="rec-sheet__in">{children}</div>
      </div>
    </>
  )
}

// ---------- the photos and clips behind you ----------
export function GallerySheet({ items, cur, story, onPick, onGo, onRemove, onTrim, onClose }: {
  items: Item[]; cur: number; story: Story
  onPick: () => void; onGo: (i: number) => void; onRemove: (i: number) => void; onTrim: (i: number) => void; onClose: () => void
}) {
  const [q, setQ] = useState(story.headline)
  const n = items.length
  return (
    <Sheet onClose={onClose} label="Photos and clips">
      <h3>Gallery</h3>
      <p className="rec-sub">Up to {MAX_ITEMS} photos or clips for this story, swiped while you talk. Kept on this phone, never uploaded.</p>
      <div className="rec-slots">
        {items.map((it, i) => (
          <div key={it.id} className={`rec-slot${i === cur ? ' on' : ''}`} style={{ backgroundImage: it.thumb ? `url(${it.thumb})` : undefined }} onClick={() => onGo(i)}>
            <span className="k">{it.type === 'img' ? 'Photo' : mmss(clipLen(it))}</span>
            {it.type === 'video' && <button className="t" type="button" onClick={(e) => { e.stopPropagation(); onTrim(i) }}>Trim</button>}
            <button className="x" type="button" aria-label="Remove" onClick={(e) => { e.stopPropagation(); onRemove(i) }}>×</button>
          </div>
        ))}
        {n < MAX_ITEMS && <button type="button" className="rec-slot add" aria-label="Add" onClick={onPick}>+</button>}
      </div>
      <div className="rec-eb">Add</div>
      <button className="rec-btn amber" type="button" onClick={onPick} disabled={n >= MAX_ITEMS}>From your phone · photos or clips</button>
      <div className="rec-field">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search Google images for" />
        <a className="rec-btn" href={`https://www.google.com/search?tbm=isch&tbs=il:cl&q=${encodeURIComponent(q)}`} target="_blank" rel="noopener noreferrer">Search Google</a>
      </div>
      <div className="rec-rights"><b>Use a picture you have the right to use.</b> The search opens with Google’s usage-rights filter on. Save the one you want to your phone, then add it here. Your own photos and clips are always safest.</div>
    </Sheet>
  )
}

// ---------- the space is full: clear some to record again (Brian, Sep 30) ----------
/**
 * Every story with its gallery and its takes, biggest first, each with Clear or Discard, and a bar filling back down.
 * Locked while full. `then` is what waits for the room (Record); without it the button says Done.
 */
export function FullSheet({ storyId, titles, then, onCleared, onClose, toast }: {
  storyId: string
  titles: Record<string, string>
  then: (() => void) | null
  onCleared: (x: StoreEntry) => void
  onClose: () => void
  toast: (msg: ReactNode, ms?: number) => void
}) {
  const [list, setList] = useState<StoreEntry[] | null>(null)
  const load = () => { void recordStore.usage().then(setList) }
  useEffect(load, [])
  if (!list) return null
  const u = usedBytes(list), full = u >= STORE_BUDGET
  const by: Record<string, StoreEntry[]> = {}
  list.forEach((x) => { (by[x.story] = by[x.story] || []).push(x) })
  const stories = Object.keys(by).sort((a, b) => usedBytes(by[b]) - usedBytes(by[a]))
  const clear = async (x: StoreEntry) => {
    await recordStore.clearEntry(x)
    onCleared(x)
    toast(x.kind === 'gallery' ? 'Gallery cleared.' : 'Take discarded.')
    load()
  }
  return (
    <Sheet onClose={onClose} lock={full} label="GoGrov’s space">
      <h3>{full ? 'Your phone’s space for GoGrov is full' : 'There’s room again'}</h3>
      <p className="rec-sub">{full ? 'Every take and gallery is kept until you clear it. Clear some to record again. Save a take to Photos first if you want to keep it.' : 'You can record again.'}</p>
      <div className={`rec-meter${full ? ' over' : ''}`}><i style={{ width: `${Math.min(100, (u / STORE_BUDGET) * 100).toFixed(1)}%` }} /></div>
      <div className="rec-meterl"><span>{fmtBytes(u)} used</span><span>{fmtBytes(STORE_BUDGET)}</span></div>
      <div className="rec-store">
        {stories.length ? stories.map((k) => (
          <div className="s" key={k}>
            <h4>{titles[k] || 'Another story'}{k === storyId && <small>This story</small>}</h4>
            {[...by[k]].sort((a, b) => b.bytes - a.bytes).map((x) => (
              <div className="r" key={`${x.kind}-${x.id ?? ''}`}>
                <span>{x.kind === 'gallery' ? `Gallery · ${x.n} item${x.n === 1 ? '' : 's'}` : `Take · ${mmss(x.dur || 0)}`}</span>
                <em>{fmtBytes(x.bytes)}</em>
                <button type="button" onClick={() => void clear(x)}>{x.kind === 'gallery' ? 'Clear' : 'Discard'}</button>
              </div>
            ))}
          </div>
        )) : <p className="rec-sub">Nothing kept yet.</p>}
      </div>
      <div className="rec-row2">
        {full && <button className="rec-btn" type="button" onClick={() => { onClose(); toast('Record waits until there’s room.') }}>Not now</button>}
        <button className="rec-btn amber" type="button" disabled={full} onClick={() => { onClose(); then?.() }}>{then ? 'Record' : 'Done'}</button>
      </div>
    </Sheet>
  )
}

// ---------- the optional trim: clips play up to 2 minutes ----------
export function TrimSheet({ item, long, onDone }: { item: Item; long: boolean; onDone: (start: number, end: number) => void }) {
  const d = item.dur || 0
  const [s, setS] = useState(item.start || 0)
  const [e, setE] = useState(item.end || Math.min(d, MAX_CLIP))
  const tv = useRef<HTMLVideoElement>(null)
  const sync = (w: 's' | 'e', ns: number, ne: number) => {
    if (ne - ns > MAX_CLIP) { if (w === 's') ne = ns + MAX_CLIP; else ns = ne - MAX_CLIP }
    if (ne < ns + 0.5) { if (w === 's') ns = Math.max(0, ne - 0.5); else ne = Math.min(d, ns + 0.5) }
    setS(ns); setE(ne)
    try { if (tv.current) tv.current.currentTime = w === 'e' ? ne : ns } catch { /* */ }
  }
  return (
    <Sheet onClose={() => onDone(item.start, item.end)} label="Trim the clip">
      <h3>Trim the clip</h3>
      <p className="rec-sub">{long ? `This clip is ${mmss(d)}. Clips play up to 2 minutes; ` : ''}Keep only the part you’ll react to. Optional.</p>
      <div className="rec-trim">
        <video ref={tv} src={item.url} muted playsInline controls />
        <label>Start <input type="range" min={0} max={d} step={0.1} value={s} onChange={(ev) => sync('s', +ev.target.value, e)} /><span>{mmss(s)}</span></label>
        <label>End <input type="range" min={0} max={d} step={0.1} value={e} onChange={(ev) => sync('e', s, +ev.target.value)} /><span>{mmss(e)} · {mmss(e - s)}</span></label>
      </div>
      <div className="rec-row2">
        <button className="rec-btn" type="button" onClick={() => onDone(0, Math.min(d, MAX_CLIP))}>Keep it all</button>
        <button className="rec-btn amber" type="button" onClick={() => onDone(s, e)}>Keep this part</button>
      </div>
    </Sheet>
  )
}

// ---------- after the take ----------
const APPS: ShareApp[] = ['instagram', 'tiktok', 'youtube']
const Lock = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
)

export interface TakeSheetProps {
  take: StoredTake
  story: Story
  toast: (msg: ReactNode, ms?: number) => void
  onSaved: (take: StoredTake) => void
  onRetake: () => void
  onDone: () => void
  onPosted: (caption: string) => Promise<void>
  /** The scrim behind I posted it's question closes the sheets. */
  onClose: () => void
}

export function TakeSheet({ take, story, toast, onSaved, onRetake, onDone, onPosted, onClose }: TakeSheetProps) {
  const [style, setStyle] = useState<ShareApp>('instagram')
  const [mine, setMine] = useState<StoredTake[]>([])
  const [installed, setInstalled] = useState<Partial<Record<ShareApp, boolean>> | null>(null)
  const [muted, setMuted] = useState(true)
  const [view, setView] = useState<'take' | 'posted'>('take')
  const [playing, setPlaying] = useState<StoredTake | null>(null)
  // Saved to Photos, remembered for the story: I posted it then offers to remove GoGrov's copy
  const savedKey = `gogrov.saved.${story.id}`
  const [saved, setSavedState] = useState(() => pref(savedKey, '') !== '')
  const setSaved = (v: boolean) => { setSavedState(v); if (v) setPref(savedKey, String(Date.now())) }
  const [busy, setBusy] = useState(false)

  useEffect(() => { void recordStore.takesFor(story.id).then(setMine) }, [story.id, take.id])
  useEffect(() => { void installedApps().then(setInstalled) }, [])

  const url = useMemo(() => (take.blob ? URL.createObjectURL(take.blob) : null), [take.blob])
  const playUrl = useMemo(() => (playing?.blob ? URL.createObjectURL(playing.blob) : null), [playing])
  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])
  useEffect(() => () => { if (playUrl) URL.revokeObjectURL(playUrl) }, [playUrl])

  const n = Math.max(1, mine.findIndex((t) => t.id === take.id) + 1 || mine.length)
  const ext = take.blob && /mp4/.test(take.blob.type) ? 'mp4' : 'webm'
  const slug = story.headline.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40).replace(/-$/, '')
  const name = `gogrov-${slug}-take${n}.${ext}`
  const caption = captionFor(style, story)
  const appName = SHARE_APPS[style].name

  const save = async () => {
    if (busy) return
    if (!take.blob) {
      await copyText(caption)
      toast(<><b>{appName} caption copied.</b> This browser can’t record the video.</>)
      onSaved(take); setSaved(true)
      return
    }
    setBusy(true)
    if (canSaveToPhotos()) toast('Saving to Photos…', 5000)
    const r = await saveVideo(take.blob, { name, caption })
    setBusy(false)
    if (r === 'failed') { toast(<><b>Couldn’t save to Photos.</b> Allow GoGrov to add to Photos in Settings.</>, 4200); return }
    setSaved(true); onSaved(take)
    if (r === 'photos') toast(<><b>Saved to Photos.</b> {appName} caption copied. Now tap the app.</>, 3200)
    else if (r === 'shared') toast(<><b>{appName} caption copied.</b> Save the video from the share sheet.</>, 3200)
    else toast(<><b>Video downloaded.</b> {appName} caption copied.</>, 3200)
  }

  const tapApp = async (k: ShareApp) => {
    setStyle(k)
    const r = await openApp(k, captionFor(k, story))
    const nm = SHARE_APPS[k].name
    if (r === 'app') toast(<><b>{nm} caption copied.</b> Opening {nm}…</>, 2600)
    else if (r === 'store') toast(<><b>{nm} caption copied.</b> Opening the App Store…</>, 2600)
    else toast(<><b>{nm} caption copied.</b></>)
  }

  const discard = async (t: StoredTake) => {
    if (t.id == null) return
    await recordStore.deleteTake(t.id)
    setMine((m) => m.filter((x) => x.id !== t.id))
    if (playing?.id === t.id) setPlaying(null)
    toast('Take discarded.')
  }

  const filePosted = async (removeCopy: boolean) => {
    if (removeCopy) await recordStore.clearStory(story.id)
    await onPosted(caption)
  }

  if (view === 'posted') {
    return (
      <Sheet onClose={onClose} label="Posted">
        {saved ? (
          <>
            <h3>Remove GoGrov’s copy?</h3>
            <p className="rec-sub">It’s safe in your Photos. The story, its caption and the day you posted it stay in Library › Posted.</p>
            <button className="rec-btn amber wide" type="button" onClick={() => void filePosted(true)}>Remove GoGrov’s copy</button>
            <div className="rec-row2"><button className="rec-btn" type="button" onClick={() => void filePosted(false)}>Keep it here too</button></div>
          </>
        ) : (
          <>
            <h3>Save it to Photos first</h3>
            <p className="rec-sub">That’s where TikTok and Instagram find it. Once it’s posted, GoGrov doesn’t need its own copy.</p>
            <button className="rec-btn amber wide" type="button" onClick={async () => { await save(); setTimeout(() => void filePosted(true), 400) }}>Save to Photos</button>
            <div className="rec-row2"><button className="rec-btn" type="button" onClick={() => void filePosted(false)}>It’s posted, keep GoGrov’s copy</button></div>
          </>
        )}
      </Sheet>
    )
  }

  return (
    <Sheet onClose={() => undefined} lock label="Recording complete">
      <div className="rec-eb">Take {n} · {mmss(take.dur)} · {take.land ? 'horizontal' : 'vertical'} · {take.mode === 'green' ? 'green screen' : 'split screen'}</div>
      <h3>Recording complete</h3>
      <div className="rec-take">
        {playing ? (
          playUrl ? <video key={playUrl} src={playUrl} controls autoPlay playsInline /> : <img src={playing.poster} alt="" />
        ) : url ? (
          <>
            <video src={url} autoPlay muted={muted} loop playsInline />
            <button type="button" className="snd2" onClick={() => setMuted((m) => !m)}>{muted ? 'Sound on' : 'Sound off'}</button>
          </>
        ) : (
          <img src={take.poster} alt="The last frame of the take" />
        )}
      </div>
      {!url && <p className="rec-sub">This browser can’t record the composite, so here is the last frame. In the app the take is a video.</p>}
      <button className="rec-btn amber wide big" type="button" onClick={() => void save()} disabled={busy}>Save video + copy caption</button>
      <p className="rec-safe"><Lock /><span><b>Nothing has been posted.</b> This video is only on your phone until you post it yourself.</span></p>
      <button className="rec-btn" type="button" onClick={onRetake}>Record another take · this one is kept</button>
      <div className="rec-eb">Post it · tap the app: its caption is copied and the app opens</div>
      <div className="rec-apps" aria-label="Post it">
        {APPS.map((k) => (
          <button key={k} type="button" aria-pressed={style === k} aria-label={`Copy the ${SHARE_APPS[k].name} caption and open ${SHARE_APPS[k].name}`} onClick={() => void tapApp(k)}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d={BRAND_PATHS[k]} /></svg>
            <span>{SHARE_APPS[k].name}</span>
            <small>{installed ? (installed[k] ? 'Open' : 'Get the app') : 'Open on the web'}</small>
          </button>
        ))}
      </div>
      <div className="rec-cap">{caption}</div>
      <div className="rec-row2">
        <button className="rec-btn" type="button" onClick={() => { void copyText(caption); toast('Caption copied.') }}>Copy caption</button>
      </div>
      {mine.length > 1 && (
        <>
          <div className="rec-eb">Your takes for this story</div>
          <div className="rec-takes">
            {mine.map((t, i) => (
              <div className="t" key={t.id}>
                <b>Take {i + 1}</b>{mmss(t.dur)}
                <button className="play" type="button" onClick={() => setPlaying(t)}>Play</button>
                <button type="button" onClick={() => void discard(t)}>Discard</button>
              </div>
            ))}
          </div>
        </>
      )}
      <p className="rec-sub" style={{ margin: 0 }}>Save it to Photos, then post it from TikTok, Instagram or YouTube: they pick it up from your Photos. Once it’s posted, GoGrov doesn’t need its own copy.</p>
      <div className="rec-row2">
        <button className="rec-btn" type="button" onClick={() => setView('posted')}>I posted it</button>
        <button className="rec-btn amber" type="button" onClick={onDone}>Done</button>
      </div>
    </Sheet>
  )
}
